import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clearSession } from '../../auth/authStore';
import { extractRole } from '../../auth/roles';
import FlikLogo from '../../components/Logo/FlikLogo';
import FlikSelect from '../../components/Select/FlikSelect';
import { GoogleIcon } from '../AuthenticatePage/SocialIcons';
import ConfirmCodeModal from './ConfirmCodeModal';
import {
  getMyProfile,
  getPreferences,
  updateProfile,
  updatePreferences,
  changePassword,
  confirmPasswordChange,
  requestAccountDeletion,
  confirmAccountDeletion,
  logout,
  ApiError,
} from '../../api/client';
import './ProfilePage.css';

/**
 * Profile page (route "/perfil"), reached from the avatar button on the home
 * screen. Lets the signed-in user review and edit their personal data
 * (PUT /users/me), toggle privacy preferences (/users/me/preferences), change
 * their password and delete their account (both two-step, email-code flows).
 *
 * Decorative figures (stats, member-since, badge progress) come from the
 * profile payload when present; when the profile can't be loaded we fall back
 * to a demo persona so the layout still reads like the design during dev.
 */

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const GENRES = ['Mujer', 'Hombre', 'No binario', 'Otro', 'Prefiero no decirlo'];

const DEFAULT_PREFS = {
  publicProfile: true,
  showRoutes: true,
  notificationsEnabled: false,
};

/**
 * Empty profile used only to keep the page rendering when the profile endpoint
 * can't be reached (e.g. the backend is down). It carries no data — every field
 * is blank and the figures fall back to "—" — so the user never sees fabricated
 * personal details. Shaped like normalizeProfile's output.
 */
const EMPTY_PROFILE = {
  name: '',
  surname: '',
  username: '',
  email: '',
  birthday: null,
  genre: '',
  biography: '',
  location: '',
  createdAt: null,
  authProvider: null,
  role: null,
  stats: { routes: null, ferratas: null, km: null },
  badges: { done: null, total: null },
};

const NAV_ITEMS = [
  { id: 'mapa', label: 'Mapa', icon: MapIcon },
  { id: 'favoritos', label: 'Favoritos', icon: StarIcon },
  { id: 'perfil', label: 'Perfil', icon: UserIcon },
];

const AUTH_PROVIDERS = [
  { id: 'GOOGLE', label: 'Google', icon: GoogleIcon }
];

/* ------------------------------ helpers -------------------------------- */

const pick = (...vals) => vals.find((v) => v !== undefined && v !== null);

function normalizeProfile(u) {
  if (!u) return null;
  return {
    name: pick(u.name, '') || '',
    surname: pick(u.surname, '') || '',
    username: pick(u.username, u.handle, '') || '',
    email: pick(u.email, '') || '',
    birthday: pick(u.birthday, u.birthDate, u.dateOfBirth, null),
    genre: pick(u.genre, u.gender, '') || '',
    biography: pick(u.biography, u.bio, '') || '',
    location: pick(u.location, u.city, '') || '',
    createdAt: pick(u.createdAt, u.memberSince, u.registeredAt, u.joinedAt, null),
    authProvider: pick(u.authProvider, u.provider, null),
    role: extractRole(u),
    stats: {
      routes: pick(u.routesCount, u.stats?.routes, null),
      ferratas: pick(u.ferratasCount, u.stats?.ferratas, null),
      km: pick(u.totalKm, u.stats?.km, null),
    },
    badges: {
      done: pick(u.badgesDone, u.badges?.done, null),
      total: pick(u.badgesTotal, u.badges?.total, null),
    },
  };
}

function normalizePrefs(p) {
  if (!p) return DEFAULT_PREFS;
  const bool = (v, fallback) => (typeof v === 'boolean' ? v : fallback);
  return {
    publicProfile: bool(p.publicProfile, DEFAULT_PREFS.publicProfile),
    showRoutes: bool(p.showRoutes, DEFAULT_PREFS.showRoutes),
    notificationsEnabled: bool(
      p.notificationsEnabled,
      DEFAULT_PREFS.notificationsEnabled
    ),
  };
}

/** Splits an ISO `yyyy-mm-dd` into { day, month, year } string parts. */
function splitIso(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  if (!m) return { day: '', month: '', year: '' };
  return { day: String(Number(m[3])), month: String(Number(m[2])), year: m[1] };
}

/** Builds an ISO `yyyy-mm-dd`, or null if the parts aren't a real date. */
function toIsoDate({ day, month, year }) {
  const d = Number(day);
  const mo = Number(month);
  const y = Number(year);
  if (!d || !mo || !y) return null;
  const date = new Date(y, mo - 1, d);
  const valid =
    date.getFullYear() === y && date.getMonth() === mo - 1 && date.getDate() === d;
  if (!valid) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${y}-${pad(mo)}-${pad(d)}`;
}

/** ISO date → "14 marzo 1994" (human, Spanish). */
function formatBirthday(iso) {
  const { day, month, year } = splitIso(iso);
  if (!day) return '—';
  return `${day} ${MONTHS[Number(month) - 1].toLowerCase()} ${year}`;
}

/** ISO date → "MAR 2024" for the "member since" badge. */
function formatMemberSince(iso) {
  const { month, year } = splitIso(iso);
  if (!year) return null;
  return `${MONTHS[Number(month) - 1].slice(0, 3).toUpperCase()} ${year}`;
}

function initialsOf(profile) {
  const a = profile.name?.trim()?.[0] || '';
  const b = profile.surname?.trim()?.[0] || '';
  return (a + b).toUpperCase() || (profile.username?.[0] || '?').toUpperCase();
}

const statOrDash = (v) => (v === null || v === undefined ? '—' : v);

/* ------------------------------ component ------------------------------ */

function ProfilePage() {
  const navigate = useNavigate();
  const premium = process.env.REACT_APP_PREMIUM_ENABLED === 'true';

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [banner, setBanner] = useState('');
  const [notice, setNotice] = useState('');
  const [activeNav, setActiveNav] = useState('perfil');

  // Modal state machine: null | 'pw-form' | 'pw-code' | 'del-confirm' | 'del-code'
  const [modal, setModal] = useState(null);
  // Error surfaced when the profile can't be loaded from the backend. Shown as a
  // modal (see LoadErrorModal) with a description and a retry action.
  const [loadError, setLoadError] = useState('');
  const [retrying, setRetrying] = useState(false);

  const loadProfile = useCallback(async () => {
    const [pRes, prefRes] = await Promise.allSettled([
      getMyProfile(),
      getPreferences(),
    ]);
    const loaded =
      pRes.status === 'fulfilled' && pRes.value
        ? normalizeProfile(pRes.value)
        : null;
    // Never leave the page on a null profile — fall back to an empty profile
    // (no fabricated data) so the layout still renders when the backend is
    // unavailable.
    setProfile(loaded || EMPTY_PROFILE);
    if (prefRes.status === 'fulfilled' && prefRes.value) {
      setPrefs(normalizePrefs(prefRes.value));
    }
    // Surface the profile-load failure (the page's primary call). ApiError
    // already carries a translated, human description.
    if (pRes.status === 'rejected') {
      const reason = pRes.reason;
      // 401/403 mean the session is invalid or expired: there's nothing to
      // retry, so drop the session and send the user to the login instead of
      // showing the error modal.
      if (reason instanceof ApiError && (reason.status === 401 || reason.status === 403)) {
        clearSession();
        navigate('/authenticate?tab=login', { replace: true });
        return;
      }
      setLoadError(
        reason instanceof ApiError
          ? reason.detail || reason.message
          : 'No hemos podido cargar tu perfil. Comprueba tu conexión e inténtalo de nuevo.'
      );
    } else {
      setLoadError('');
    }
  }, [navigate]);

  useEffect(() => {
    let alive = true;
    (async () => {
      await loadProfile();
      if (alive) setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [loadProfile]);

  const retryLoad = async () => {
    setRetrying(true);
    await loadProfile();
    setRetrying(false);
  };

  const memberSince = useMemo(
    () => (profile ? formatMemberSince(profile.createdAt) : null),
    [profile]
  );

  const handleLogout = async () => {
    await logout();
    navigate('/authenticate?tab=login', { replace: true });
  };

  const handleNav = (id) => {
    if (id === 'perfil') {
      navigate('/perfil');
    } else {
      navigate('/');
    }
    setActiveNav(id);
  };

  /* ---- Datos personales (edit / save) ---- */

  const startEditing = () => {
    setBanner('');
    setNotice('');
    setForm({
      name: profile.name,
      surname: profile.surname,
      genre: profile.genre,
      biography: profile.biography,
      ...splitIso(profile.birthday),
    });
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setForm(null);
    setBanner('');
  };

  const updateField = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const setGenre = (genre) =>
    setForm((prev) => ({ ...prev, genre: prev.genre === genre ? '' : genre }));

  const handleSaveProfile = async (event) => {
    event.preventDefault();
    setBanner('');

    const anyDatePart = form.day || form.month || form.year;
    const iso = toIsoDate(form);
    if (anyDatePart && !iso) {
      setBanner('La fecha de nacimiento no es válida.');
      return;
    }
    if (iso && new Date(iso) >= new Date()) {
      setBanner('La fecha de nacimiento debe ser anterior a hoy.');
      return;
    }

    setSavingProfile(true);
    try {
      const payload = {
        name: form.name.trim() || null,
        surname: form.surname.trim() || null,
        birthday: iso,
        genre: form.genre || null,
        biography: form.biography.trim() || null,
      };
      const updated = await updateProfile(payload);
      // Prefer the server's echo; fall back to the optimistic local values.
      setProfile((prev) =>
        updated ? { ...prev, ...normalizeProfile(updated) } : { ...prev, ...payload, birthday: iso }
      );
      setEditing(false);
      setForm(null);
      setNotice('Datos actualizados.');
    } catch (err) {
      setBanner(
        err instanceof ApiError
          ? err.detail || err.message
          : 'No hemos podido guardar los cambios. Inténtalo de nuevo.'
      );
    } finally {
      setSavingProfile(false);
    }
  };

  /* ---- Privacy toggles ---- */

  const togglePref = (key) => async () => {
    const next = !prefs[key];
    const prev = prefs;
    setPrefs((p) => ({ ...p, [key]: next })); // optimistic
    setNotice('');
    try {
      const saved = await updatePreferences({ [key]: next });
      if (saved) setPrefs(normalizePrefs(saved));
    } catch (err) {
      setPrefs(prev); // revert
      setBanner(
        err instanceof ApiError
          ? err.detail || err.message
          : 'No hemos podido actualizar tus preferencias.'
      );
    }
  };

  if (loading) {
    return (
      <div className="Profile Profile--loading">
        <FlikLogo height={22} />
        <p>Cargando tu perfil…</p>
      </div>
    );
  }

  const fullName =
    [profile.name, profile.surname].filter(Boolean).join(' ') || profile.username;

  return (
    <div className="Profile">
      {/* ---- Top bar ---- */}
      <header className="Profile__topbar">
        <button
          type="button"
          className="Profile__logoBtn"
          onClick={() => navigate('/')}
          aria-label="Volver al mapa"
        >
          <FlikLogo height={20} />
        </button>
        <div className="Profile__topActions">
          {premium && (
            <button type="button" className="Profile__premium">
              Hazte Premium
            </button>
          )}
          {profile?.role === 'ADMIN' && (
            <button
              type="button"
              className="Profile__admin"
              onClick={() => navigate('/admin')}
            >
              <ShieldIcon />
              Admin
            </button>
          )}
          <button type="button" className="Profile__logout" onClick={handleLogout}>
            Cerrar sesión
          </button>
        </div>
      </header>

      {notice && <p className="Profile__toast" role="status">{notice}</p>}

      <div className="Profile__grid">
        {/* ============ Left column ============ */}
        <aside className="Profile__aside">
          <section className="Card IdentityCard">
            <div className="IdentityCard__avatar" aria-hidden="true">
              <span>{initialsOf(profile)}</span>
              <span className="IdentityCard__avatarEdit" title="Cambiar foto">
                <PencilIcon />
              </span>
            </div>
            <h1 className="IdentityCard__name">{fullName}</h1>
            <p className="IdentityCard__handle">
              @{profile.username}
              {profile.location ? ` · ${profile.location}` : ''}
            </p>
            {memberSince && (
              <span className="IdentityCard__since">Miembro desde {memberSince}</span>
            )}

            <dl className="IdentityCard__stats">
              <div>
                <dt>{statOrDash(profile.stats.routes)}</dt>
                <dd>Rutas</dd>
              </div>
              <div>
                <dt>{statOrDash(profile.stats.ferratas)}</dt>
                <dd>Ferratas</dd>
              </div>
              <div>
                <dt>{statOrDash(profile.stats.km)}</dt>
                <dd>km</dd>
              </div>
            </dl>
          </section>

          <section className="Card PrivacyCard">
            <h2 className="Card__eyebrow">Privacidad</h2>
            <PrivacyRow
              label="Perfil público"
              checked={prefs.publicProfile}
              onToggle={togglePref('publicProfile')}
            />
            <PrivacyRow
              label="Mostrar mis rutas"
              checked={prefs.showRoutes}
              onToggle={togglePref('showRoutes')}
            />
            <PrivacyRow
              label="Notificaciones"
              checked={prefs.notificationsEnabled}
              onToggle={togglePref('notificationsEnabled')}
            />
          </section>
        </aside>

        {/* ============ Main column ============ */}
        <main className="Profile__main">
          {/* ---- Datos personales ---- */}
          <section className="Card DataCard">
            <div className="Card__head">
              <h2 className="Card__title">Datos personales</h2>
              {!editing && (
                <button type="button" className="Btn Btn--soft" onClick={startEditing}>
                  Editar
                </button>
              )}
            </div>

            {!editing ? (
              <dl className="DataView">
                <Field label="Nombre">{fullName}</Field>
                <Field label="Usuario" locked>@{profile.username}</Field>
                <Field label="Correo" locked>{profile.email || '—'}</Field>
                <Field label="Fecha de nacimiento">
                  {formatBirthday(profile.birthday)}
                </Field>
                <Field label="Género">{profile.genre || '—'}</Field>
                <Field label="Sobre mí" full>
                  {profile.biography || '—'}
                </Field>
              </dl>
            ) : (
              <form className="DataForm" onSubmit={handleSaveProfile} noValidate>
                <div className="DataForm__grid">
                  <div className="FormField">
                    <label className="FormLabel" htmlFor="pf-name">Nombre</label>
                    <input
                      id="pf-name"
                      className="FormInput"
                      type="text"
                      autoComplete="given-name"
                      value={form.name}
                      onChange={updateField('name')}
                    />
                  </div>
                  <div className="FormField">
                    <label className="FormLabel" htmlFor="pf-surname">Apellidos</label>
                    <input
                      id="pf-surname"
                      className="FormInput"
                      type="text"
                      autoComplete="family-name"
                      value={form.surname}
                      onChange={updateField('surname')}
                    />
                  </div>

                  <div className="FormField">
                    <span className="FormLabel">Usuario</span>
                    <div className="FormLocked" title="El usuario no se puede cambiar aquí">
                      @{profile.username}
                      <LockIcon />
                    </div>
                  </div>
                  <div className="FormField">
                    <span className="FormLabel">Correo</span>
                    <div className="FormLocked" title="El correo no se puede cambiar aquí">
                      {profile.email || '—'}
                      <LockIcon />
                    </div>
                  </div>

                  <div className="FormField FormField--full">
                    <label className="FormLabel" htmlFor="pf-day">Fecha de nacimiento</label>
                    <div className="FormDate">
                      <input
                        id="pf-day"
                        className="FormInput"
                        type="text"
                        inputMode="numeric"
                        maxLength={2}
                        placeholder="DD"
                        aria-label="Día"
                        value={form.day}
                        onChange={updateField('day')}
                      />
                      <FlikSelect
                        variant="line"
                        ariaLabel="Mes"
                        placeholder="Mes"
                        value={form.month}
                        onChange={(v) =>
                          updateField('month')({ target: { value: v } })
                        }
                        options={MONTHS.map((label, i) => ({
                          value: String(i + 1),
                          label,
                        }))}
                      />
                      <input
                        className="FormInput"
                        type="text"
                        inputMode="numeric"
                        maxLength={4}
                        placeholder="AAAA"
                        aria-label="Año"
                        value={form.year}
                        onChange={updateField('year')}
                      />
                    </div>
                  </div>

                  <div className="FormField FormField--full">
                    <span className="FormLabel">Género</span>
                    <div className="FormPills" role="group" aria-label="Género">
                      {GENRES.map((g) => (
                        <button
                          key={g}
                          type="button"
                          className={`FormPill${form.genre === g ? ' is-active' : ''}`}
                          aria-pressed={form.genre === g}
                          onClick={() => setGenre(g)}
                        >
                          {g}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="FormField FormField--full">
                    <label className="FormLabel" htmlFor="pf-bio">Sobre mí</label>
                    <textarea
                      id="pf-bio"
                      className="FormInput FormTextarea"
                      rows={3}
                      maxLength={280}
                      value={form.biography}
                      onChange={updateField('biography')}
                    />
                  </div>
                </div>

                {banner && <p className="FormBanner">{banner}</p>}

                <div className="DataForm__actions">
                  <button
                    type="submit"
                    className="Btn Btn--primary"
                    disabled={savingProfile}
                  >
                    {savingProfile ? 'Guardando…' : 'Guardar cambios'}
                  </button>
                  <button
                    type="button"
                    className="Btn Btn--ghost"
                    onClick={cancelEditing}
                    disabled={savingProfile}
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            )}
          </section>

          <div className="Profile__lowerGrid">
            {/* ---- Progreso de insignias ---- */}
            <section className="Card BadgesCard">
              <h2 className="Card__eyebrow Card__eyebrow--onDark">
                Progreso de insignias
              </h2>
              <div className="BadgesCard__count">
                <strong>{statOrDash(profile.badges.done)}</strong>
                <span>/ {statOrDash(profile.badges.total)} retos</span>
              </div>
              <div
                className="BadgesCard__bar"
                role="progressbar"
                aria-valuenow={profile.badges.done || 0}
                aria-valuemin={0}
                aria-valuemax={profile.badges.total || 0}
              >
                <span
                  className="BadgesCard__barFill"
                  style={{
                    width:
                      profile.badges.total
                        ? `${Math.min(
                            100,
                            (100 * (profile.badges.done || 0)) / profile.badges.total
                          )}%`
                        : '0%',
                  }}
                />
              </div>
              <div className="BadgesCard__row" aria-hidden="true">
                <span className="Badge Badge--gold"><FlagIcon /></span>
                <span className="Badge Badge--gold"><MountainIcon /></span>
                <span className="Badge Badge--gold"><StarIcon /></span>
                <span className="Badge Badge--locked"><LockIcon /></span>
              </div>
            </section>

            {/* ---- Cuenta ---- */}
            <section className="Card AccountCard">
              <h2 className="Card__eyebrow">Cuenta</h2>
              <ul className="AccountCard__list">
                <li>
                  <button
                    type="button"
                    className="AccountRow"
                    onClick={() => {
                      setBanner('');
                      setModal('pw-form');
                    }}
                  >
                    <span>Cambiar contraseña</span>
                    <ChevronIcon />
                  </button>
                </li>
                <li>
                  <button type="button" className="AccountRow">
                    <span>Cuentas vinculadas</span>
                    <div className="AccountRow__meta">
                      {(() => {
                        if (!profile.authProvider) return 'No vinculada';
                        const provider = AUTH_PROVIDERS.find(
                          (p) => p.id === profile.authProvider
                        );
                        if (!provider) return profile.authProvider;
                        const Icon = provider.icon;
                        return <Icon className="AccountRow__icon" />;
                      })()}
                    </div>
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="AccountRow"
                    onClick={() =>
                      setNotice('Preparando tu descarga… te avisaremos por correo.')
                    }
                  >
                    <span>Descargar mis datos</span>
                    <ChevronIcon />
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="AccountRow AccountRow--danger"
                    onClick={() => {
                      setBanner('');
                      setModal('del-confirm');
                    }}
                  >
                    <span>Eliminar cuenta</span>
                    <ChevronIcon />
                  </button>
                </li>
              </ul>
            </section>
          </div>
        </main>
      </div>

      {/* ---- Mobile bottom nav ---- */}
      <nav className="Home__nav" aria-label="Navegación principal">
        {NAV_ITEMS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            className={`Home__navItem${activeNav === id ? ' is-active' : ''}`}
            aria-current={activeNav === id ? 'page' : undefined}
            onClick={() => handleNav(id)}
          >
            <Icon />
            <span>{label}</span>
          </button>
        ))}
      </nav>

      {/* ===================== Modals ===================== */}
      {modal === 'pw-form' && (
        <ChangePasswordModal
          onClose={() => setModal(null)}
          onSent={() => setModal('pw-code')}
        />
      )}

      {modal === 'pw-code' && (
        <ConfirmCodeModal
          title="Confirma el cambio"
          description="Te hemos enviado un código de 6 dígitos a tu correo. Introdúcelo para aplicar la nueva contraseña."
          confirmLabel="Cambiar contraseña"
          onConfirm={async (code) => {
            await confirmPasswordChange({ code });
            setModal(null);
            setNotice('Tu contraseña se ha actualizado.');
          }}
          onClose={() => setModal(null)}
        />
      )}

      {modal === 'del-confirm' && (
        <DeleteConfirmModal
          onClose={() => setModal(null)}
          onProceed={() => setModal('del-code')}
        />
      )}

      {modal === 'del-code' && (
        <ConfirmCodeModal
          title="Confirma la eliminación"
          description="Te hemos enviado un código de 6 dígitos a tu correo. Introdúcelo para eliminar tu cuenta de forma permanente."
          confirmLabel="Eliminar definitivamente"
          danger
          onConfirm={async (code) => {
            await confirmAccountDeletion({ code });
            clearSession();
            navigate('/authenticate?tab=login', { replace: true });
          }}
          onClose={() => setModal(null)}
        />
      )}

      {loadError && (
        <LoadErrorModal
          description={loadError}
          retrying={retrying}
          onRetry={retryLoad}
          onClose={() => setLoadError('')}
        />
      )}
    </div>
  );
}

/* --------------------- small presentational pieces --------------------- */

function PrivacyRow({ label, checked, onToggle }) {
  return (
    <div className="PrivacyRow">
      <span>{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        className={`Toggle${checked ? ' is-on' : ''}`}
        onClick={onToggle}
      >
        <span className="Toggle__dot" />
      </button>
    </div>
  );
}

function Field({ label, children, locked = false, full = false }) {
  return (
    <div className={`DataField${full ? ' DataField--full' : ''}`}>
      <dt className="DataField__label">
        {label}
        {locked && <LockIcon />}
      </dt>
      <dd className="DataField__value">{children}</dd>
    </div>
  );
}

/**
 * Shown when the profile can't be loaded from the backend. Keeps the app's modal
 * design and surfaces a human description of the error, with a retry action.
 */
function LoadErrorModal({ description, retrying, onRetry, onClose }) {
  return (
    <div className="Modal" role="presentation" onMouseDown={onClose}>
      <div
        className="Modal__card"
        role="alertdialog"
        aria-modal="true"
        aria-label="Error al cargar el perfil"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="Modal__close"
          onClick={onClose}
          aria-label="Cerrar"
          disabled={retrying}
        >
          ×
        </button>
        <h3 className="Modal__title">No hemos podido cargar tu perfil</h3>
        <p className="Modal__desc">
          Ha ocurrido un problema al obtener tus datos del servidor.
        </p>

        <p className="Modal__error">{description}</p>

        <div className="Modal__actions">
          <button
            type="button"
            className="Btn Btn--ghost"
            onClick={onClose}
            disabled={retrying}
          >
            Cerrar
          </button>
          <button
            type="button"
            className="Btn Btn--primary"
            onClick={onRetry}
            disabled={retrying}
          >
            {retrying ? 'Reintentando…' : 'Reintentar'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Step 1 of the password change: current + new password. On success the backend
 * emails a code (202) and we hand off to the ConfirmCodeModal.
 */
function ChangePasswordModal({ onClose, onSent }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [reveal, setReveal] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    if (!current) return setError('Introduce tu contraseña actual.');
    if (next.length < 8)
      return setError('La nueva contraseña debe tener al menos 8 caracteres.');
    if (next !== confirm) return setError('Las contraseñas nuevas no coinciden.');

    setSubmitting(true);
    try {
      await changePassword({ currentPassword: current, newPassword: next });
      onSent();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.detail || err.message
          : 'No hemos podido iniciar el cambio. Inténtalo de nuevo.'
      );
      setSubmitting(false);
    }
  };

  return (
    <div className="Modal" role="presentation" onMouseDown={onClose}>
      <div
        className="Modal__card"
        role="dialog"
        aria-modal="true"
        aria-label="Cambiar contraseña"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="Modal__close"
          onClick={onClose}
          aria-label="Cerrar"
          disabled={submitting}
        >
          ×
        </button>
        <h3 className="Modal__title">Cambiar contraseña</h3>
        <p className="Modal__desc">
          Confirmaremos el cambio con un código enviado a tu correo.
        </p>

        <form onSubmit={submit} noValidate>
          <div className="FormField">
            <label className="FormLabel" htmlFor="pw-current">Contraseña actual</label>
            <input
              id="pw-current"
              className="FormInput"
              type={reveal ? 'text' : 'password'}
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          </div>
          <div className="FormField">
            <label className="FormLabel" htmlFor="pw-new">Nueva contraseña</label>
            <input
              id="pw-new"
              className="FormInput"
              type={reveal ? 'text' : 'password'}
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
          </div>
          <div className="FormField">
            <label className="FormLabel" htmlFor="pw-confirm">Repite la nueva contraseña</label>
            <input
              id="pw-confirm"
              className="FormInput"
              type={reveal ? 'text' : 'password'}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </div>

          <label className="Modal__reveal">
            <input
              type="checkbox"
              checked={reveal}
              onChange={(e) => setReveal(e.target.checked)}
            />
            Mostrar contraseñas
          </label>

          {error && <p className="Modal__error">{error}</p>}

          <div className="Modal__actions">
            <button
              type="button"
              className="Btn Btn--ghost"
              onClick={onClose}
              disabled={submitting}
            >
              Cancelar
            </button>
            <button type="submit" className="Btn Btn--primary" disabled={submitting}>
              {submitting ? 'Enviando código…' : 'Continuar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Step 1 of deletion: a hard warning before we email the confirmation code. */
function DeleteConfirmModal({ onClose, onProceed }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const proceed = async () => {
    setSubmitting(true);
    setError('');
    try {
      await requestAccountDeletion();
      onProceed();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.detail || err.message
          : 'No hemos podido iniciar el proceso. Inténtalo de nuevo.'
      );
      setSubmitting(false);
    }
  };

  return (
    <div className="Modal" role="presentation" onMouseDown={onClose}>
      <div
        className="Modal__card"
        role="dialog"
        aria-modal="true"
        aria-label="Eliminar cuenta"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="Modal__close"
          onClick={onClose}
          aria-label="Cerrar"
          disabled={submitting}
        >
          ×
        </button>
        <h3 className="Modal__title">¿Eliminar tu cuenta?</h3>
        <p className="Modal__desc">
          Esta acción es permanente: perderás tus rutas, retos e insignias. Para
          continuar te enviaremos un código de confirmación a tu correo.
        </p>

        {error && <p className="Modal__error">{error}</p>}

        <div className="Modal__actions">
          <button
            type="button"
            className="Btn Btn--ghost"
            onClick={onClose}
            disabled={submitting}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="Btn Btn--danger"
            onClick={proceed}
            disabled={submitting}
          >
            {submitting ? 'Enviando código…' : 'Sí, continuar'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------- icons --------------------------------- */

function PencilIcon() {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true">
      <path
        d="M4 20h4L18.5 9.5a2 2 0 0 0 0-2.8l-1.2-1.2a2 2 0 0 0-2.8 0L4 16v4Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
      <path
        d="M12 3 5 6v5c0 4.4 3 8.4 7 10 4-1.6 7-5.6 7-10V6l-7-3Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="m9 12 2 2 4-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true">
      <rect x="5" y="10" width="14" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" fill="none" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path d="m9 6 6 6-6 6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path
        d="m12 3 2.6 5.6 6.1.7-4.5 4.2 1.2 6-5.4-3-5.4 3 1.2-6L3.3 9.3l6.1-.7L12 3Z"
        fill="currentColor"
      />
    </svg>
  );
}

function FlagIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path
        d="M5 21V4m0 1h11l-2 4 2 4H5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MountainIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path
        d="m3 19 6-11 4 7 2-3 6 7H3Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MapIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path
        d="m9 4-6 2v14l6-2 6 2 6-2V4l-6 2-6-2Zm0 0v14m6-12v14"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <circle cx="12" cy="8" r="3.6" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <path
        d="M5 20c0-3.6 3.1-6 7-6s7 2.4 7 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default ProfilePage;
